// 9fbef606107a605d69c0edbcd8029e5d
import React from 'react';
import PropTypes from 'prop-types';
import BodyCopy from '@tcp/core/src/components/common/atoms/BodyCopy';
import Image from '@tcp/core/src/components/common/atoms/Image';
import { mobileOrderDateFormatted } from '@tcp/core/src/utils/utils';
import { getTranslatedDate } from '@tcp/core/src/components/features/account/Orders/molecules/OrderListWithDetails/views/OrderListWithDetails.utils';
import { getRCStatus } from '@tcp/core/src/components/features/account/OrderDetails/organism/OrderDetailsWrapper/views/OrderDetailsWrapper.utils';
import {
  getLogoIconPath,
  getStatusTextClass,
} from '../../OrderDetailsWrapper/views/OrderDetailsWrapper.utils';
import OrderStatus from '../../OrderStatus';
import constants from '../../../OrderDetails.constants';
import {
  getDynamicLabelValues,
  getLabelFromCopyOrFromTranslation,
  getLabelWithDefaultValue,
  renderLabel,
} from '../../../../../../../utils/labels/labelUtilty';
import {
  ACCOUNT_ORDERS_LABEL,
  OrderDetailLabels,
  lblBeingProcessed,
  lblHeadingOutForDelivery,
  lblInitiatedNotification,
  lblOrderDetailsShipped,
  lblOrderPartiallyShipped,
  lblOrderReceived,
  lblOrdersOn,
  lblOrdersStatusItemsPickedUp,
  lblOrdersStatusOrderExpiredAndRefunded,
  lblOrdersStatusOrderPickUp,
  lblOrdersStatusOrderPicked,
  lblOrdersStatusOrderShipped,
  lblShippedToStore,
  lblStatusItemsDelivered,
  lblStatusItemsInTransit,
  lblStatusItemsReceived,
  lblStatusOrderExpired,
  lblStatusOrderInProcess,
  lblStatusOrderReadyForPickup,
  lblStatusShippingCancelled,
  lblStatusUserCallNeeded,
} from '../../../container/OrderDetails.labels';

const { STATUS_CONSTANTS = {} } = constants;
const {
  PROCESSING,
  ORDER_IN_PROCESS,
  ORDER_BEING_PROCESSED,
  SHIPPED,
  DELIVERED,
  ITEMS_RECEIVED,
  ITEM_READY_FOR_PICK_UP,
  CALL_NEEDED,
  IN_TRANSIT,
  OUT_FOR_DELIVERY,
  CANCELLED,
  EXPIRED,
  PARTIALLY_SHIPPED,
  ORDER_PICKED,
  ITEMS_READY_FOR_PICKUP,
  ITEMS_PICKED_UP,
  ORDER_PICKED_UP,
  EXPIRED_AND_REFUNDED,
  ORDER_PROCESSING_AT_FACILITY,
  IN_PROCESS,
} = STATUS_CONSTANTS;

const displayPickedUpDate = (pickedUpDate, orderLabels) => {
  return pickedUpDate && pickedUpDate.toLowerCase() !== 'n/a' ? (
    <BodyCopy component="p" className="shipment-count">
      {`${getLabelWithDefaultValue(orderLabels, lblOrdersOn)} ${getTranslatedDate(
        pickedUpDate,
        true
      )}`}
    </BodyCopy>
  ) : null;
};

const getEstimateDate = (estimatedDate) =>
  estimatedDate ? mobileOrderDateFormatted(estimatedDate) : '';

const getStatusText = (
  status = '',
  orderLabels,
  isBossOrder = false,
  isShowBossOrderStatusEnabled = false
) => {
  const normalized = status.trim().toLowerCase();
  const bossEnabled = isBossOrder && isShowBossOrderStatusEnabled;

  const DEFAULT_LABELS = {
    [PROCESSING]: lblBeingProcessed,
    [ORDER_IN_PROCESS]: lblBeingProcessed,
    [ORDER_BEING_PROCESSED]: lblBeingProcessed,
    [SHIPPED]: lblOrdersStatusOrderShipped,
    [DELIVERED]: lblStatusItemsDelivered,
    [ITEMS_RECEIVED]: lblStatusItemsReceived,
    [ITEM_READY_FOR_PICK_UP]: lblStatusOrderReadyForPickup,
    [CALL_NEEDED]: lblStatusUserCallNeeded,
    [IN_TRANSIT]: lblStatusItemsInTransit,
    [OUT_FOR_DELIVERY]: lblHeadingOutForDelivery,
    [CANCELLED]: lblStatusShippingCancelled,
    [EXPIRED]: lblStatusOrderExpired,
    [IN_PROCESS]: lblStatusOrderInProcess,
    [PARTIALLY_SHIPPED]: lblOrderPartiallyShipped,
    [ORDER_PICKED]: lblOrdersStatusOrderPicked,
    [ITEMS_READY_FOR_PICKUP]: lblStatusOrderReadyForPickup,
    [ITEMS_PICKED_UP]: lblOrdersStatusItemsPickedUp,
    [ORDER_PICKED_UP]: lblOrdersStatusOrderPickUp,
    [EXPIRED_AND_REFUNDED]: lblOrdersStatusOrderExpiredAndRefunded,
    [ORDER_PROCESSING_AT_FACILITY]: lblBeingProcessed,
  };

  const BOSS_OVERRIDES = {
    [PROCESSING]: lblOrderReceived,
    [ORDER_IN_PROCESS]: lblOrderReceived,
    [ORDER_BEING_PROCESSED]: lblOrderReceived,
    [SHIPPED]: lblShippedToStore,
  };

  const labelKey =
    bossEnabled && BOSS_OVERRIDES[normalized]
      ? BOSS_OVERRIDES[normalized]
      : DEFAULT_LABELS[normalized];

  return labelKey ? getLabelWithDefaultValue(orderLabels, labelKey) : status;
};

const renderStatusText = ({
  returnedOrder,
  returnInitiatedOrder,
  status,
  statusText,
  orderLabels,
  extObj,
  isShowBossOrderStatusEnabled,
}) => {
  return returnedOrder || returnInitiatedOrder
    ? status
    : getStatusText(statusText, orderLabels, extObj.isNonEcom, isShowBossOrderStatusEnabled);
};

const displayOrderDate = ({
  index,
  totalCount,
  extObj,
  isShowBossOrderStatusEnabled,
  orderLabels,
  label,
  displayDate,
}) => {
  return index &&
    index > 0 &&
    totalCount &&
    ((!extObj.isNonEcom && !isShowBossOrderStatusEnabled) ||
      (isShowBossOrderStatusEnabled && !extObj.isBopisOrder)) ? (
    <BodyCopy component="p" className="shipment-count">
      {`${getLabelWithDefaultValue(orderLabels, lblOrderDetailsShipped)} ${index} of ${totalCount}`}
      {label && displayDate && (
        <>
          <span className="elem-mr-XXS elem-ml-XXS">|</span>
          <span className="elem-mr-XS delivered">{`${label} ${displayDate}`}</span>
        </>
      )}
    </BodyCopy>
  ) : null;
};

const OrderItemHeader = ({
  status,
  index,
  totalCount,
  returnedOrder,
  orderGroup,
  extObj,
  estimatedDeliveryDate,
  actualDeliveryDate,
  carrierDeliveryDate,
  returnInitiatedOrder,
  estimatedShipmentDate,
  isShowBossOrderStatusEnabled,
  orderDate,
}) => {
  const statusText = status.toLowerCase();
  const actualDeliveryDateValue = getEstimateDate(actualDeliveryDate);
  const carrierDeliveryDateValue = getEstimateDate(carrierDeliveryDate);
  const estimatedDeliveryDateValue = getEstimateDate(estimatedDeliveryDate);
  const estimatedShipmentDateValue = getEstimateDate(estimatedShipmentDate);
  const shipDateDateValue = getEstimateDate(orderGroup?.shipDate);
  const orderDateValue = getEstimateDate(orderDate);

  const orderLabels = getLabelFromCopyOrFromTranslation(ACCOUNT_ORDERS_LABEL, OrderDetailLabels);

  const { label, displayDate } =
    getRCStatus(
      status,
      estimatedDeliveryDateValue,
      actualDeliveryDateValue,
      shipDateDateValue,
      carrierDeliveryDateValue,
      orderLabels,
      estimatedShipmentDateValue,
      isShowBossOrderStatusEnabled,
      extObj?.isNonEcom,
      orderDateValue
    ) || {};
  return (
    <div className="order-status-container">
      <div className="order-status-logo">
        <Image src={getLogoIconPath(statusText)} />
      </div>
      <div className="order-status-text">
        <BodyCopy
          component="p"
          fontWeight="bold"
          fontFamily="secondary"
          className={`${getStatusTextClass(statusText)} ${
            returnedOrder || returnInitiatedOrder ? 'no-text-transform elem-mb-XS' : ''
          }`}
          fontSize="fs18"
        >
          {renderStatusText({
            returnedOrder,
            returnInitiatedOrder,
            status,
            statusText,
            orderLabels,
            extObj,
            isShowBossOrderStatusEnabled,
          })}
        </BodyCopy>

        {displayOrderDate({
          index,
          totalCount,
          extObj,
          isShowBossOrderStatusEnabled,
          orderLabels,
          label,
          displayDate,
        })}
        {returnedOrder ? (
          <BodyCopy
            component="p"
            fontSize={['fs14', 'fs12', 'fs14']}
            fontFamily="secondary"
            fontWeight="semibold"
          >
            {renderLabel(
              getDynamicLabelValues('dynamic.account_d.lbl_orders_returnedNotification'),
              'lbl_orders_returnedNotification'
            )}
          </BodyCopy>
        ) : null}
        {returnInitiatedOrder ? (
          <BodyCopy
            component="p"
            fontSize={['fs14', 'fs12', 'fs14']}
            fontFamily="secondary"
            fontWeight="semibold"
          >
            {`${getLabelWithDefaultValue(orderLabels, lblInitiatedNotification)}`}
          </BodyCopy>
        ) : null}
        {displayPickedUpDate(extObj?.pickedUpDate, orderLabels)}
      </div>
      {orderGroup && (
        <div className="order-status-cta hide-on-mobile">
          <OrderStatus
            status={orderGroup?.status}
            trackingNumber={orderGroup?.trackingNbr}
            trackingUrl={orderGroup?.trackingUrl}
            shippedDate={orderGroup?.shipDate}
            isBopisOrder={extObj && extObj.isBopisOrder}
            showNumAsLink
          />
        </div>
      )}
    </div>
  );
};

OrderItemHeader.propTypes = {
  status: PropTypes.string,
  index: PropTypes.number,
  totalCount: PropTypes.number,
  returnedOrder: PropTypes.bool,
  orderGroup: PropTypes.shape([]),
  extObj: PropTypes.shape({}),
  estimatedDeliveryDate: PropTypes.string,
  actualDeliveryDate: PropTypes.string,
  carrierDeliveryDate: PropTypes.string,
  returnInitiatedOrder: PropTypes.bool,
  estimatedShipmentDate: PropTypes.string,
};

OrderItemHeader.defaultProps = {
  status: '',
  index: 0,
  totalCount: 0,
  returnedOrder: false,
  returnInitiatedOrder: false,
  orderGroup: [],
  extObj: {},
  estimatedDeliveryDate: '',
  actualDeliveryDate: '',
  carrierDeliveryDate: '',
  estimatedShipmentDate: '',
};

export default OrderItemHeader;
